import type { CSSProperties, ReactElement, ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';

import { Heading, Text } from '../Typography';
import {
  frameStyles,
  groupStyles,
  tableStyles,
  titleMetaStyles,
  titleStyles,
} from './ResponsiveTable.styles';
import type { ResponsiveTableProps } from './ResponsiveTable.types';

const stickyOffsetValue = (value: number | string | undefined) =>
  typeof value === 'number' ? `${value}px` : value ?? '0px';

const isEmptyValue = (value: ReactNode) =>
  value === null || value === undefined || value === false || value === '';

/**
 * Publishes the rendered title and column-header heights so the header can
 * stick directly below the title, and so the title releases at the same
 * scroll position as the header (see groupStyles).
 */
const useStickyMeasurements = (enabled: boolean) => {
  const groupRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    const group = groupRef.current;
    if (!enabled || !group) return undefined;

    const update = () => {
      const titleHeight = titleRef.current?.offsetHeight ?? 0;
      const headHeight = headRef.current?.offsetHeight ?? 0;
      group.style.setProperty(
        '--responsive-table-title-height',
        `${titleHeight}px`
      );
      // A visually hidden header (stacked cards) does not occupy the stack.
      group.style.setProperty(
        '--responsive-table-head-height',
        `${headHeight > 1 ? headHeight : 0}px`
      );
    };

    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    if (titleRef.current) observer.observe(titleRef.current);
    if (headRef.current) observer.observe(headRef.current);
    return () => observer.disconnect();
  }, [enabled]);

  return { groupRef, titleRef, headRef };
};

const ResponsiveTable = <Row,>({
  rows,
  columns,
  getRowKey,
  ariaLabel,
  className,
  renderRowFooter,
  title,
  titleMeta,
  titleAs = 'h3',
  emptyValue = '—',
  responsive = 'none',
  stickyHeader = false,
  stickyOffset,
}: ResponsiveTableProps<Row>): ReactElement => {
  const isSticky = stickyHeader && responsive !== 'scroll';
  const hasTitle = !isEmptyValue(title);
  const hasRowHeader = columns.some((column) => column.isRowHeader);
  const { groupRef, titleRef, headRef } = useStickyMeasurements(isSticky);

  const style = {
    '--responsive-table-sticky-offset': stickyOffsetValue(stickyOffset),
  } as CSSProperties;

  return (
    <div
      className={className}
      css={groupStyles}
      data-has-title={hasTitle || undefined}
      data-responsive={responsive}
      data-sticky-header={isSticky}
      ref={groupRef}
      style={style}
    >
      {hasTitle && (
        <div css={titleStyles} data-responsive-table-title ref={titleRef}>
          <Heading as={titleAs} fontWeight="bold" size="2xs">
            {title}
          </Heading>
          {!isEmptyValue(titleMeta) && (
            <Text css={titleMetaStyles} size="sm">
              {titleMeta}
            </Text>
          )}
        </div>
      )}
      <div
        aria-label={
          responsive === 'scroll' ? `${ariaLabel} scroll area` : undefined
        }
        css={frameStyles}
        data-responsive-table-frame
        role={responsive === 'scroll' ? 'region' : undefined}
        tabIndex={responsive === 'scroll' ? 0 : undefined}
      >
        <table aria-label={ariaLabel} css={tableStyles}>
          <colgroup>
            {columns.map((column) => (
              <col key={column.id} style={{ width: column.width }} />
            ))}
          </colgroup>
          <thead ref={headRef}>
            <tr>
              {columns.map((column) => (
                <th
                  data-align={column.align ?? 'start'}
                  key={column.id}
                  scope="col"
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          {rows.map((row, rowIndex) => {
            const footer = renderRowFooter?.(row);
            const rowKey = getRowKey(row, rowIndex);
            return (
              <tbody
                data-responsive-table-row-key={String(rowKey)}
                key={rowKey}
              >
                <tr
                  data-has-row-header={hasRowHeader || undefined}
                  data-responsive-table-row
                >
                  {columns.map((column) => {
                    const Cell = column.isRowHeader ? 'th' : 'td';
                    const value = column.render(row);
                    const isEmpty = isEmptyValue(value);
                    const mobileLabel =
                      column.mobile?.label === undefined
                        ? column.header
                        : column.mobile.label;

                    return (
                      <Cell
                        data-align={column.align ?? 'start'}
                        data-empty={isEmpty || undefined}
                        data-mobile-hidden={
                          column.mobile?.hidden ||
                          (isEmpty && column.mobile?.hideWhenEmpty) ||
                          undefined
                        }
                        data-mobile-placement={
                          column.mobile?.placement ?? 'body'
                        }
                        data-row-header={column.isRowHeader || undefined}
                        key={column.id}
                        scope={column.isRowHeader ? 'row' : undefined}
                      >
                        {mobileLabel !== null && (
                          <span
                            aria-hidden="true"
                            data-responsive-table-mobile-label
                          >
                            {mobileLabel}
                          </span>
                        )}
                        <span data-responsive-table-value>
                          {isEmpty ? emptyValue : value}
                        </span>
                      </Cell>
                    );
                  })}
                </tr>
                {!isEmptyValue(footer) && (
                  <tr data-responsive-table-footer>
                    <td colSpan={columns.length}>{footer}</td>
                  </tr>
                )}
              </tbody>
            );
          })}
        </table>
      </div>
    </div>
  );
};

ResponsiveTable.displayName = 'ResponsiveTable';

export default ResponsiveTable;
